import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';

/**
 * Video Context Service
 * Manages video-level learning and context
 */

/**
 * Initialize or get video context
 * @param {string} videoId - Video ID
 * @returns {Object} Video context data
 */
export async function getVideoContext(videoId) {
  try {
    const contextRef = doc(db, 'videoContext', videoId);
    const contextDoc = await getDoc(contextRef);
    
    if (contextDoc.exists()) {
      console.log(`­ƒôè [VideoContext] Retrieved context for video: ${videoId}`);
      return { id: contextDoc.id, ...contextDoc.data() };
    }
    
    // Initialize empty context
    const initialContext = {
      videoId,
      inferredVideoGoal: '',
      dominantAudienceLevel: '',
      topIntents: {},
      topFrictionThemes: {},
      unresolvedQuestions: {},
      confidenceScore: 0,
      lastUpdated: new Date(),
      createdAt: new Date()
    };
    
    await setDoc(contextRef, initialContext);
    console.log(`Ô£à [VideoContext] Initialized context for video: ${videoId}`);
    return initialContext;
  } catch (error) {
    console.error('ÔØî [VideoContext] Error getting context:', error);
    throw error;
  }
}

/**
 * Update video context with new data
 * @param {string} videoId - Video ID
 * @param {Object} updates - Context updates
 */
export async function updateVideoContext(videoId, updates) {
  try {
    const contextRef = doc(db, 'videoContext', videoId);
    
    await updateDoc(contextRef, {
      ...updates,
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [VideoContext] Updated context for video: ${videoId}`);
  } catch (error) {
    console.error('ÔØî [VideoContext] Error updating context:', error);
    throw error;
  }
}

/**
 * Increment intent count for a video
 * @param {string} videoId - Video ID
 * @param {string} intent - Intent type
 */
export async function incrementVideoIntent(videoId, intent) {
  try {
    const contextRef = doc(db, 'videoContext', videoId);
    
    await updateDoc(contextRef, {
      [`topIntents.${intent}`]: increment(1),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [VideoContext] Incremented intent '${intent}' for video: ${videoId}`);
  } catch (error) {
    console.error('ÔØî [VideoContext] Error incrementing intent:', error);
    throw error;
  }
}

/**
 * Increment friction theme count for a video
 * @param {string} videoId - Video ID
 * @param {string} theme - Friction theme
 */
export async function incrementFrictionTheme(videoId, theme) {
  try {
    const contextRef = doc(db, 'videoContext', videoId);
    
    await updateDoc(contextRef, {
      [`topFrictionThemes.${theme}`]: increment(1),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [VideoContext] Incremented friction theme '${theme}' for video: ${videoId}`);
  } catch (error) {
    console.error('ÔØî [VideoContext] Error incrementing friction theme:', error);
    throw error;
  }
}

/**
 * Increment unresolved question topic for a video
 * @param {string} videoId - Video ID
 * @param {string} topic - Question topic
 */
export async function incrementUnresolvedQuestion(videoId, topic) {
  try {
    const contextRef = doc(db, 'videoContext', videoId);
    
    await updateDoc(contextRef, {
      [`unresolvedQuestions.${topic}`]: increment(1),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [VideoContext] Incremented unresolved question '${topic}' for video: ${videoId}`);
  } catch (error) {
    console.error('ÔØî [VideoContext] Error incrementing question:', error);
    throw error;
  }
}

/**
 * Update video context confidence score
 * @param {string} videoId - Video ID
 * @param {number} newScore - New confidence score
 */
export async function updateVideoConfidence(videoId, newScore) {
  try {
    const contextRef = doc(db, 'videoContext', videoId);
    
    await updateDoc(contextRef, {
      confidenceScore: Math.min(1, Math.max(0, newScore)),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [VideoContext] Updated confidence score for video: ${videoId} to ${newScore}`);
  } catch (error) {
    console.error('ÔØî [VideoContext] Error updating confidence:', error);
    throw error;
  }
}

/**
 * Calculate average confidence across all signals for a video
 * @param {Array} signals - Array of comment signals
 * @returns {number} Average confidence score
 */
export function calculateAverageConfidence(signals) {
  if (!signals || signals.length === 0) return 0;
  
  const totalConfidence = signals.reduce((sum, signal) => sum + (signal.confidence || 0), 0);
  return totalConfidence / signals.length;
}

/**
 * Analyze and update video context from signals
 * @param {string} videoId - Video ID
 * @param {Array} signals - Array of comment signals
 */
export async function analyzeAndUpdateVideoContext(videoId, signals) {
  try {
    const context = await getVideoContext(videoId);
    
    // Aggregate intents
    const intentCounts = {};
    const frictionThemes = {};
    const questions = {};
    
    signals.forEach(signal => {
      // Count intents
      if (signal.intent) {
        intentCounts[signal.intent] = (intentCounts[signal.intent] || 0) + 1;
      }
      
      // Count friction themes from entities
      if (signal.intent === 'friction' && signal.entities) {
        signal.entities.forEach(entity => {
          frictionThemes[entity] = (frictionThemes[entity] || 0) + 1;
        });
      }
      
      // Count question topics
      if (signal.intent === 'question' && signal.entities) {
        signal.entities.forEach(entity => {
          questions[entity] = (questions[entity] || 0) + 1;
        });
      }
    });
    
    // Calculate average confidence
    const avgConfidence = calculateAverageConfidence(signals);
    
    // Update context
    await updateVideoContext(videoId, {
      topIntents: intentCounts,
      topFrictionThemes: frictionThemes,
      unresolvedQuestions: questions,
      confidenceScore: avgConfidence
    });
    
    console.log(`Ô£à [VideoContext] Analyzed and updated context for video: ${videoId}`);
    return {
      topIntents: intentCounts,
      topFrictionThemes: frictionThemes,
      unresolvedQuestions: questions,
      confidenceScore: avgConfidence
    };
  } catch (error) {
    console.error('ÔØî [VideoContext] Error analyzing context:', error);
    throw error;
  }
}

